import { forwardRef, useEffect, useRef, useState, type ComponentPropsWithoutRef } from 'react';
import { IconKey, IconLogout, IconUserScan, IconX } from '@tabler/icons-react';
import { Dropdown } from 'antd';
import { useFela } from 'react-fela';
import { FormattedMessage, useIntl } from 'react-intl';

import { config } from '@/config/config';
import { env } from '@/env';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useLogout } from '@/modules/auth/components/LogoutButton/hooks/useLogout';
import { useIsAdmin, useUser } from '@/modules/auth/hooks';
import { ALL_USERS_GROUP_ID } from '@/modules/groups';
import { StaticModal, useModal } from '@/modules/modals';
import { SecretsViewModal } from '@/modules/secrets/components/SecretsViewModal';
import { useViewFilter } from '@/modules/secrets/hooks/useViewFilter';
import { assignedToMeViewFilter } from '@/modules/secrets/utilities/assignedToMeViewFilter';
import { deriveViewState } from '@/modules/secrets/utilities/deriveViewState';
import { Link } from '@/modules/ui';

import { ShowRecordsMenu } from './ShowRecordsMenu';
import { UserIdentity } from './UserIdentity';
import * as felaRules from './UserMenu.rules';

export const UserMenu = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(({ className, ...rest }, ref) => {
    // `open` controls the AntD popup mount; `expanded` drives the CSS height
    // animation. They are decoupled so the collapse can play before unmount.
    const [open, setOpen] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [triggerSize, setTriggerSize] = useState<{ width: number; height: number } | null>(null);
    const cardRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
    const { css } = useFela({ open });

    useEffect(() => () => clearTimeout(closeTimer.current), []);

    useEffect(() => {
        if (!open) {
            setExpanded(false);
            return;
        }
        // Expand on the next frame so the transition runs from the collapsed state.
        const frame = requestAnimationFrame(() => setExpanded(true));
        return () => cancelAnimationFrame(frame);
    }, [open]);

    const intl = useIntl();
    const isMin768 = useBreakpoint('sm');
    const user = useUser();
    const isAdmin = useIsAdmin();
    const { viewFilter, setView } = useViewFilter();
    const { mutate: logout, isPending } = useLogout();
    const { modal, updateModal } = useModal(StaticModal.VIEW_FILTER);

    const viewState = deriveViewState(viewFilter, user?.id);
    const viewingAsOther = viewState === 'other';
    const targetName =
        viewFilter?.id === ALL_USERS_GROUP_ID ? intl.formatMessage({ id: 'secret.groups.allUsers.label' }) : viewFilter?.name;
    const displayName = viewingAsOther ? targetName : user?.name;

    const showModal = isAdmin && !env.VITE_LIMIT_ADMIN_PERMISSIONS;

    const requestClose = () => {
        clearTimeout(closeTimer.current);
        setExpanded(false);
        // Unmount only after the collapse animation has played.
        closeTimer.current = setTimeout(() => setOpen(false), felaRules.MENU_ANIM_MS);
    };

    const handleOpenChange = (next: boolean) => {
        clearTimeout(closeTimer.current);
        if (next) {
            if (cardRef.current) {
                const rect = cardRef.current.getBoundingClientRect();
                setTriggerSize({ width: rect.width, height: rect.height });
            }
            setOpen(true);
        } else {
            requestClose();
        }
    };

    const identity = <UserIdentity user={user} viewingAsOther={viewingAsOther} displayName={displayName} open={open} />;

    return (
        <div ref={ref} className={css(felaRules.root, className)} {...rest}>
            {showModal && (
                <SecretsViewModal
                    open={modal?.visibility ?? false}
                    defaultViewFilter={viewFilter}
                    onSubmit={view => {
                        setView(view);
                        updateModal(false);
                    }}
                    onCancel={() => {
                        updateModal(false);
                    }}
                />
            )}
            <Dropdown
                open={open}
                onOpenChange={handleOpenChange}
                trigger={['click']}
                placement='bottomLeft'
                transitionName=''
                align={triggerSize ? { offset: [0, -triggerSize.height] } : undefined}
                popupRender={() => (
                    <div
                        ref={menuRef}
                        className={css(felaRules.menu)}
                        // On phones the popup keeps the full-width card size instead of the 290px flyout
                        style={{ width: expanded && isMin768 ? 290 : (triggerSize?.width ?? 290) }}
                    >
                        <button type='button' className={css(felaRules.cardHeader)} onClick={requestClose}>
                            {identity}
                        </button>
                        <div className={css(felaRules.itemsWrapper, expanded && felaRules.itemsWrapperExpanded)}>
                            <div className={css(felaRules.itemsInner)}>
                                <div className={css(felaRules.itemsContent)}>
                                    {viewingAsOther ? (
                                        <div className={css(felaRules.itemsGroup)}>
                                            <button
                                                type='button'
                                                className={css(felaRules.item, felaRules.destructiveItem)}
                                                onClick={() => {
                                                    setView(user ? assignedToMeViewFilter(user) : null);
                                                    requestClose();
                                                }}
                                            >
                                                <IconX size={16} />
                                                <FormattedMessage id='sidebar.stopViewAsUser' />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className={css(felaRules.itemsGroup)}>
                                            {showModal && (
                                                <button
                                                    type='button'
                                                    className={css(felaRules.item)}
                                                    onClick={() => {
                                                        updateModal(true);
                                                        requestClose();
                                                    }}
                                                >
                                                    <IconUserScan size={16} />
                                                    <FormattedMessage id='sidebar.userMenu.viewAsUser' />
                                                </button>
                                            )}
                                            {isAdmin && <ShowRecordsMenu containerRef={menuRef} onChosen={requestClose} />}
                                            <Link
                                                type='router'
                                                to={config.routes.apiKeys}
                                                color='foregroundsPrimary'
                                                customStyle={felaRules.linkItem}
                                                onClick={requestClose}
                                            >
                                                <IconKey size={16} />
                                                <FormattedMessage id='menu.api-keys' />
                                            </Link>
                                        </div>
                                    )}
                                    <span className={css(felaRules.separator)} />
                                    <button
                                        type='button'
                                        className={css(felaRules.item, felaRules.destructiveItem)}
                                        disabled={isPending}
                                        onClick={() => {
                                            logout();
                                            requestClose();
                                        }}
                                    >
                                        <IconLogout size={16} />
                                        <FormattedMessage id='menu.logout' />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            >
                <button ref={cardRef} type='button' className={css(felaRules.card)} aria-label={displayName}>
                    {identity}
                </button>
            </Dropdown>
        </div>
    );
});
